import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowLeft, Presentation, Maximize, Minimize, Share2, FileDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { SlideRenderer } from "@/slides/SlideRenderer";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ProgressBar } from "@/components/ProgressBar";
import { PresenterNotes } from "@/components/PresenterNotes";
import { ShareDialog } from "@/components/ShareDialog";

const EASE = [0.85, 0, 0.15, 1];

const ToolbarButton = ({ onClick, active, children, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    title={label}
    aria-label={label}
    className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium tracking-wide backdrop-blur-xl transition-all hover:-translate-y-0.5 ${
      active
        ? "border-champagne bg-champagne text-navy"
        : "border-white/10 bg-white/5 text-cream/80 hover:text-cream hover:border-champagne/40"
    }`}
  >
    {children}
  </button>
);

export default function DeckView() {
  const { mode } = useParams();
  const navigate = useNavigate();
  const { t, lang, presenter, togglePresenter, setPresenter, isFullscreen, toggleFullscreen } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const validMode = ["short", "full", "tutorial"].includes(mode) ? mode : "full";
  const slides = t.decks[validMode];
  const total = slides.length;

  const initialSlide = Math.min(Math.max(parseInt(searchParams.get("slide") || "0", 10) || 0, 0), total - 1);
  const [[index, direction], setIndex] = useState([initialSlide, 0]);
  const [shareOpen, setShareOpen] = useState(false);
  const touchStart = useRef(null);

  const paginate = useCallback(
    (dir) => {
      setIndex(([cur]) => {
        const next = Math.min(Math.max(cur + dir, 0), total - 1);
        return [next, dir];
      });
    },
    [total]
  );

  // sync slide index to URL for shareability
  useEffect(() => {
    const sp = new URLSearchParams(searchParams);
    sp.set("slide", String(index));
    sp.set("lang", lang);
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, lang]);

  // clamp when language/mode changes total
  useEffect(() => {
    setIndex(([cur, d]) => [Math.min(cur, total - 1), d]);
  }, [total]);

  const share = useCallback(() => setShareOpen(true), []);

  const exportPdf = useCallback(() => {
    window.print();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowRight", " ", "PageDown"].includes(e.key)) { e.preventDefault(); paginate(1); }
      else if (["ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); paginate(-1); }
      else if (e.key.toLowerCase() === "f") { e.preventDefault(); toggleFullscreen(); }
      else if (e.key.toLowerCase() === "n") { e.preventDefault(); togglePresenter(); }
      else if (e.key === "Escape") { navigate("/"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paginate, toggleFullscreen, togglePresenter, navigate]);

  const slide = slides[index];

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60, scale: 0.98 }),
    center: { opacity: 1, x: 0, scale: 1 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60, scale: 0.96 }),
  };

  return (
    <div
      className="relative h-screen w-screen overflow-hidden bg-navy-800"
      onTouchStart={(e) => (touchStart.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchStart.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(dx) > 60) paginate(dx < 0 ? 1 : -1);
        touchStart.current = null;
      }}
      data-testid="deck-view"
    >
      {/* ambient glows */}
      <div className="pointer-events-none absolute -left-40 top-0 h-[520px] w-[520px] rounded-full bg-champagne/[0.06] blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[520px] w-[520px] rounded-full bg-coral/[0.05] blur-[120px]" />

      {/* Top toolbar */}
      <div className="no-print absolute left-0 right-0 top-0 z-40 flex items-center justify-between gap-3 px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <ToolbarButton onClick={() => navigate("/")} label={t.ui.backToMenu} testid="btn-menu">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">{t.ui.backToMenu}</span>
          </ToolbarButton>
          <span className="hidden font-serif text-sm text-cream/60 md:inline">{t.modeMeta[validMode]}</span>
        </div>
        <div className="flex items-center gap-2">
          <ToolbarButton onClick={togglePresenter} active={presenter} label={t.ui.presenter} testid="btn-presenter">
            <Presentation className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden lg:inline">{t.ui.presenter}</span>
          </ToolbarButton>
          <ToolbarButton onClick={toggleFullscreen} label={t.ui.fullscreen} testid="btn-fullscreen">
            {isFullscreen ? <Minimize className="h-4 w-4" strokeWidth={1.5} /> : <Maximize className="h-4 w-4" strokeWidth={1.5} />}
          </ToolbarButton>
          <ToolbarButton onClick={exportPdf} label={t.ui.exportPdf} testid="btn-export">
            <FileDown className="h-4 w-4" strokeWidth={1.5} />
          </ToolbarButton>
          <ToolbarButton onClick={share} label={t.ui.share} testid="btn-share">
            <Share2 className="h-4 w-4" strokeWidth={1.5} />
          </ToolbarButton>
          <LanguageSelector />
        </div>
      </div>

      {/* Slide stage */}
      <div className="no-print absolute inset-0 flex items-center justify-center px-6 py-24 sm:px-14 lg:px-24">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`${validMode}-${lang}-${index}`}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: EASE }}
            className="mx-auto h-full w-full max-w-6xl"
          >
            <div className="flex h-full items-center">
              <div className="w-full">
                <SlideRenderer slide={slide} />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Edge click zones */}
      {index > 0 && (
        <button
          onClick={() => paginate(-1)}
          className="no-print absolute left-0 top-24 bottom-16 z-30 w-[10%] cursor-w-resize"
          aria-label={t.ui.prev}
          data-testid="zone-prev"
        />
      )}
      {index < total - 1 && (
        <button
          onClick={() => paginate(1)}
          className="no-print absolute right-0 top-24 bottom-16 z-30 w-[10%] cursor-e-resize"
          aria-label={t.ui.next}
          data-testid="zone-next"
        />
      )}

      {/* Bottom controls */}
      <div className="no-print absolute bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4">
        <button
          onClick={() => paginate(-1)}
          disabled={index === 0}
          data-testid="btn-prev"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-cream backdrop-blur-xl transition-all hover:border-champagne/50 disabled:opacity-30"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
        </button>
        <span className="font-serif text-sm text-cream/70" data-testid="slide-counter">
          <span className="text-champagne">{String(index + 1).padStart(2, "0")}</span>
          <span className="mx-1.5 text-cream/30">/</span>
          {String(total).padStart(2, "0")}
        </span>
        <button
          onClick={() => paginate(1)}
          disabled={index === total - 1}
          data-testid="btn-next"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-cream backdrop-blur-xl transition-all hover:border-champagne/50 disabled:opacity-30"
        >
          <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </div>

      <ProgressBar current={index} total={total} />
      <PresenterNotes notes={slide.notes} />

      {/* Print-only full deck (Export PDF) */}
      <PrintDeck slides={slides} title={t.modeMeta[validMode]} />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        mode={validMode}
        index={index}
        slide={slide}
      />
    </div>
  );
}

const PrintDeck = ({ slides, title }) => (
  <div className="hidden print:block">
    <h1 className="mb-6 font-serif text-3xl">iFotoLife — {title}</h1>
    {slides.map((s, i) => (
      <div key={i} className="print-page mb-8 border-b pb-6">
        <div className="text-xs uppercase tracking-widest text-[#8a7a4a]">{s.overline || s.step}</div>
        <h2 className="mt-2 font-serif text-2xl">{String(s.title).replace(/\n/g, " ")}</h2>
        {s.lead && <p className="mt-2">{s.lead}</p>}
        {s.body && <p className="mt-2">{s.body}</p>}
        {s.bullets && (
          <ul className="mt-2 list-disc pl-5">
            {s.bullets.map((b, j) => (
              <li key={j}><strong>{b.title}:</strong> {b.text}</li>
            ))}
          </ul>
        )}
        {s.steps && (
          <ol className="mt-2 list-decimal pl-5">
            {s.steps.map((b, j) => (
              <li key={j}><strong>{b.title}:</strong> {b.text}</li>
            ))}
          </ol>
        )}
        {s.stats && (
          <ul className="mt-2 pl-0">
            {s.stats.map((b, j) => (
              <li key={j}><strong>{b.value}</strong> — {b.label}</li>
            ))}
          </ul>
        )}
        {s.faq && (
          <ul className="mt-2 pl-0">
            {s.faq.map((b, j) => (
              <li key={j} className="mb-1"><strong>{b.q}</strong><br />{b.a}</li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);
